import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  questionsApi,
  Question,
  QueryQuestionsParams,
  QuestionType,
  CreateQuestionDto,
} from '../../../api/questions';
import { subjectsApi, Subject, Topic } from '../../../api/subjects';
import { QuestionFormData, UseQuestionsReturn } from '../types';

const emptyForm: QuestionFormData = {
  subject_id: '',
  topic_id: null,
  type: 'simple',
  text: '',
  image: null,
  explanation: null,
  correct_text_answer: null,
  difficulty: null,
  options: [
    { label: 'A', text: '', is_correct: false, sort_order: 0 },
    { label: 'B', text: '', is_correct: false, sort_order: 1 },
    { label: 'C', text: '', is_correct: false, sort_order: 2 },
    { label: 'D', text: '', is_correct: false, sort_order: 3 },
  ],
  sub_questions: [],
};

const sanitize = (form: QuestionFormData): CreateQuestionDto => ({
  subject_id: form.subject_id,
  topic_id: form.topic_id || null,
  type: form.type || 'simple',
  text: form.text,
  image: form.image || null,
  explanation: form.explanation || null,
  correct_text_answer: form.correct_text_answer || null,
  difficulty: form.difficulty || null,
  options: (form.options || []).map((o, i) => ({
    label: o.label,
    text: o.text,
    image: o.image || null,
    is_correct: !!o.is_correct,
    sort_order: o.sort_order ?? i,
  })),
  sub_questions: (form.sub_questions || []).map((sq, sqi) => ({
    text: sq.text,
    image: sq.image || null,
    sort_order: sq.sort_order ?? sqi,
    sub_options: (sq.sub_options || []).map((so, soi) => ({
      label: so.label,
      text: so.text,
      image: so.image || null,
      is_correct: !!so.is_correct,
      sort_order: so.sort_order ?? soi,
    })),
  })),
});

export function useQuestions(): UseQuestionsReturn {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<QueryQuestionsParams>({ page: 1, limit: 20 });
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [formData, setFormData] = useState<QuestionFormData>(emptyForm);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [viewingQuestion, setViewingQuestion] = useState<Question | null>(null);

  const fetchQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await questionsApi.getAll(filters);
      setQuestions(data.data);
      setTotal(data.total);
      setPage(data.page);
      setTotalPages(data.totalPages);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch questions');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    subjectsApi
      .getAll()
      .then(setSubjects)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!filters.subject_id) {
      setTopics([]);
      return;
    }
    subjectsApi
      .getTopics(filters.subject_id)
      .then(setTopics)
      .catch(() => setTopics([]));
  }, [filters.subject_id]);

  const handleOpenCreate = () => {
    setFormData({ ...emptyForm, subject_id: filters.subject_id || '', topic_id: filters.topic_id || null });
    setIsCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.subject_id || !formData.text.trim()) {
      toast.error('Subject and question text are required');
      return;
    }
    try {
      await questionsApi.create(sanitize(formData));
      toast.success('Question created');
      setIsCreateDialogOpen(false);
      setFormData(emptyForm);
      fetchQuestions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create question');
    }
  };

  const handleEdit = (question: Question) => {
    setEditingQuestion(question);
    setFormData({
      subject_id: question.subject_id,
      topic_id: question.topic_id,
      type: question.type,
      text: question.text,
      image: question.image,
      explanation: question.explanation,
      correct_text_answer: question.correct_text_answer,
      difficulty: question.difficulty,
      options: (question.options || []).map((o) => ({
        label: o.label,
        text: o.text,
        image: o.image,
        is_correct: o.is_correct,
        sort_order: o.sort_order,
      })),
      sub_questions: (question.sub_questions || []).map((sq) => ({
        text: sq.text,
        image: sq.image,
        sort_order: sq.sort_order,
        sub_options: (sq.sub_options || []).map((so) => ({
          label: so.label,
          text: so.text,
          image: so.image,
          is_correct: so.is_correct,
          sort_order: so.sort_order,
        })),
      })),
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingQuestion) return;
    try {
      const payload = sanitize(formData);
      const { subject_id, ...rest } = payload;
      await questionsApi.update(editingQuestion.id, rest);
      toast.success('Question updated');
      setIsEditDialogOpen(false);
      setEditingQuestion(null);
      fetchQuestions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update question');
    }
  };

  const handleView = (question: Question) => {
    setViewingQuestion(question);
    setIsViewDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await questionsApi.delete(id);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete question');
    }
  };

  const handleTypeChange = (type: QuestionType) => {
    setFormData((prev) => {
      const next = { ...prev, type };
      if (type === 'complex_with_subs' || type === 'exclusive_subs') {
        if (!prev.sub_questions || prev.sub_questions.length === 0) {
          next.sub_questions = [
            {
              text: '',
              sort_order: 0,
              sub_options: [
                { label: 'A', text: '', is_correct: false, sort_order: 0 },
                { label: 'B', text: '', is_correct: false, sort_order: 1 },
              ],
            },
          ];
        }
      }
      return next;
    });
  };

  return {
    questions,
    total,
    page,
    totalPages,
    isLoading,
    filters,
    subjects,
    topics,
    isCreateDialogOpen,
    isEditDialogOpen,
    isViewDialogOpen,
    formData,
    viewingQuestion,
    editingQuestion,
    setFilters,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setIsViewDialogOpen,
    setFormData,
    handleOpenCreate,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleView,
    handleDelete,
    handleTypeChange,
  };
}
